import React, { useState, useRef, useEffect } from 'react';
import Truncate from 'react-truncate';
import * as Icons from '@seismic/icons';
import { useTranslation } from 'react-i18next';
import UxEventService from '../service/UxEventService';
import { UXContextType, getImageUrl, getFormatIcon, isMobileDevice, getCurrentManifest } from '../commonUtil/util';
import noThumbnail from '../resources/noThumbnail.png';
import { withLinkStore } from '../app/reducers';
import ViewerAvatar from './common/viewerAvatar';
import ActionIcon from './common/actionIcon';
import NewContentChip from './common/newContentChip';
import { KeyBoardAction } from '../commonUtil/constants';

const FooterForSingleDoc = (props) => {
    const { t } = useTranslation();
    const { sender } = props.linkStore;

    const mailTo = () => {
        const email = sender.email;
        location.href = `mailto:${email}`;
        UxEventService.navigateViewEvent({ Context: UXContextType.FOOTER, Target: 'Send Email' });
    };

    const handleOnKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            mailTo();
        }
    };

    return (
        <div className="seismic-engagement-viewer-footer-single-doc">
            <div className="-single-footer-conatiner">
                <div className="-sender-avatar">
                    <ViewerAvatar className="footer-avatar" sender={sender} brandingHilightColor="#EDEDED" />
                </div>
                <div className="-sender-infos">
                    <span className="sender-name"> {sender.name} </span>
                    {sender.title && !isMobileDevice.any() && <span className="sender-title"> {sender.title} </span>}
                </div>
                <div className="d-flex -email-btn"
                    onClick={mailTo}
                    onKeyDown={handleOnKeydown}
                    role="button"
                    tabIndex="0">
                    <svg width="15" height="12" viewBox="0 0 15 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M13.0093 0.541687H1.99075C1.12141 0.541687 0.416672 1.24642 0.416672 2.11576V9.43521C0.416672 10.3045 1.12141 11.0093 1.99075 11.0093H13.0093C13.8786 11.0093 14.5833 10.3045 14.5833 9.43521V2.11576C14.5833 1.24642 13.8786 0.541687 13.0093 0.541687ZM7.46527 5.55827L2.4395 1.72224H12.491L7.46527 5.55827ZM7.46527 6.87648C7.30227 6.87681 7.13989 6.81001 7.02298 6.67869L1.59723 2.53737V9.43521C1.59723 9.65254 1.77341 9.82872 1.99075 9.82872H13.0093C13.2266 9.82872 13.4028 9.65254 13.4028 9.43521V2.48435L7.90757 6.67869C7.79066 6.81001 7.62827 6.87681 7.46527 6.87648Z" fill="white" />
                    </svg>
                    <span className="email-text">{t('LIVE_SEND_VIEWER_CONTACT')}</span>
                </div>
            </div>
        </div>);
};

const FooterContentCard = (props) => {
    const { id, format, name, isShowNewChip } = props.content;
    const { linkBasicInfo, manifests, contentCovers, viewerSetting, downloadAssets } = props.linkStore;
    const { canDownload, downloadFormats, enableLiveSendDownloadOptions } = linkBasicInfo;
    const currentManifest = manifests.length > 0 && getCurrentManifest(manifests.find(mf => mf.Id === id).OriginalManifests);
    const cover = contentCovers.find(cc => cc.id === id).cover;

    const handleSelectContent = () => {
        props.onSelectContent(props.content);
        UxEventService.contentOpenEvent({ Context: UXContextType.FOOTER, Target: `Open ${props.content.format}` });
    };

    const handleKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            handleSelectContent();
        }
    };

    return (<div className="footer-content-card"
        onClick={handleSelectContent}
        tabIndex="0"
        role="button"
        aria-label={`open ${name}`}
        onKeyDown={handleKeydown}>
        <NewContentChip brandingHilightColor={viewerSetting.brandingHilightColor} isShowNewChip={isShowNewChip} />
        <div className="thumbnail-wrapper">
            <img aria-hidden="true" className="content-thumbnail" src={cover || getImageUrl(noThumbnail)} alt="content-thumbnail" />
        </div>
        <div className="content-description">
            <img className="content-format-icon"
                alt={`${format} File Type`}
                src={getFormatIcon(format, currentManifest.Url)} />
            <div className="content-title">
                <Truncate lines={2}> {name} </Truncate>
            </div>
            <ActionIcon className="footer-card-icon"
                contextType={UXContextType.FOOTER}
                canDownload={canDownload}
                content={props.content}
                currentManifest={currentManifest}
                brandingHilightColor={viewerSetting.brandingHilightColor}
                downloadFormats={downloadFormats}
                downloadAssets={downloadAssets}
                enableLiveSendDownloadOptions={enableLiveSendDownloadOptions}
                dropdownPlacement="top" />
        </div>
    </div>);
};

const CARD_WIDTH = 286;
const FooterForMultiDoc = (props) => {
    const { t } = useTranslation();
    const { onSelectContent, isOpen } = props;
    const { contents, selectedContent } = props.linkStore;

    const listWidth = CARD_WIDTH * (contents.length - 1);
    const [offsetLeft, setOffsetLeft] = useState(0);
    const [listClientWidth, setListClientWidth] = useState(0);
    const contentList = useRef();
    useEffect(() => {
        setOffsetLeft(0);
    }, [selectedContent]);
    useEffect(() => {
        // when the open/close panel animation complete, then get the cliet width of footer
        setTimeout(() => {
            setListClientWidth(contentList.current.clientWidth);
        }, 500);
    }, [isOpen]);
    const onPrevious = () => {
        if (CARD_WIDTH + offsetLeft < 0) {
            setOffsetLeft(offsetLeft + CARD_WIDTH);
        } else {
            setOffsetLeft(0);
        }
    };

    const onNext = () => {
        if (offsetLeft + listWidth > listClientWidth) {
            setOffsetLeft(offsetLeft - CARD_WIDTH);
        }
    };

    const showPrevious = offsetLeft < 0;
    const showNext = () => !contentList.current || offsetLeft + listWidth > listClientWidth;
    return (
        <div className="seismic-engagement-viewer-footer-multi-docs">
            <div className="footer-title">{contents.length === 2 ? t('LIVE_SEND_VIEWER_FOOTER_VIEW_FILE') : t('LIVE_SEND_VIEWER_FOOTER_VIEW_FILES')}</div>
            <div className="footer-content-list" ref={contentList}>
                {showPrevious && <div className="icon-previous" onClick={onPrevious}><Icons.CaretLeftIcon /></div>}
                {showNext() && <div className="icon-next" onClick={onNext}><Icons.CaretRightIcon /></div>}
                <div className="content-list-container" style={{ width: listWidth, left: offsetLeft }}>
                    {contents.filter(c => c.id !== selectedContent.id).map(c =>
                        (<FooterContentCard linkStore={props.linkStore} key={c.id} content={c} onSelectContent={onSelectContent} />))}
                </div>
            </div>
        </div>);
};

const Footer = (props) => {
    const { onSelectContent } = props;
    const { contents } = props.linkStore;

    const contentCount = contents.length;
    // enhance tablets touch move
    let touchStartPointX;
    let touchStartPointY;
    const onTouchStart = (e) => {
        touchStartPointX = e.touches[0].pageX;
        touchStartPointY = e.touches[0].pageY;
    };

    const getDirection = (startPointX, startPointY, endPointX, endPointY) => {
        const angx = endPointX - startPointX;
        const angy = endPointY - startPointY;
        let result = 0;

        if (Math.abs(angx) < 2 && Math.abs(angy) < 2) {
            return result;
        }

        // get angle
        const angle = (Math.atan2(angy, angx) * 180) / Math.PI;
        if (angle >= -135 && angle <= -45) {
            result = 1;// to up
        } else if (angle > 45 && angle < 135) {
            result = 2;// to down
        } else if ((angle >= 135 && angle <= 180) || (angle >= -180 && angle < -135)) {
            result = 3;// to left
        } else if (angle >= -45 && angle <= 45) {
            result = 4;// to right
        }

        return result;
    };

    const onTouchEnd = (e) => {
        const touchEndPointX = e.changedTouches[0].pageX;
        const touchEndPointY = e.changedTouches[0].pageY;
        const direction = getDirection(touchStartPointX, touchStartPointY, touchEndPointX, touchEndPointY);
        if (direction === 1 || direction === 2) {
            props.hideFooter();
        }
    };

    return (
        contentCount &&
        <div role="region" aria-label="view additional files" className="seismic-engagement-viewer-footer" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {contentCount === 1 && <FooterForSingleDoc linkStore={props.linkStore} />}
            {contentCount > 1 && <FooterForMultiDoc
                linkStore={props.linkStore}
                isOpen={props.isOpen}
                onSelectContent={onSelectContent} />}
        </div>
    );
};

export default withLinkStore(Footer);
